import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import AnalyticsRowsTable from "./AnalyticsRowsTable";
import copy from "../../public/locales/en/analytics.json";
import { useAppDispatch, useAppSelector } from "../store";
import {
  fetchCampaignAttribution,
  setAnalyticsCountry,
} from "../store/slices/productAnalyticsSlice";
import {
  analyticsRatio,
  campaignRowsForCountry,
  countryName,
} from "../utils/analyticsFormatting";

const labels = copy.campaign;

export default function CampaignAttributionReport(): React.ReactElement {
  const dispatch = useAppDispatch();
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const {
    days,
    attribution,
    attributionLoading,
    attributionError,
    countryFilter,
  } = useAppSelector((state) => state.productAnalytics);
  useEffect(() => {
    const request = dispatch(fetchCampaignAttribution(days));
    return (): void => request.abort();
  }, [dispatch, days]);

  const rows = campaignRowsForCountry(attribution?.rows ?? [], countryFilter);
  const totals = rows.reduce(
    (sum, row): { visits: number; registrations: number; unknown: number } => ({
      visits: sum.visits + row.visits,
      registrations: sum.registrations + row.registrations,
      unknown:
        sum.unknown +
        (!row.source && !row.referrerHost ? row.registrations : 0),
    }),
    { visits: 0, registrations: 0, unknown: 0 },
  );

  const sources = new Map<
    string,
    {
      source: string;
      visits: number;
      registrations: number;
      practiced: number;
      paid: number;
    }
  >();
  for (const row of rows) {
    const source = row.source ?? row.referrerHost ?? labels.unknown;
    const entry = sources.get(source) ?? {
      source,
      visits: 0,
      registrations: 0,
      practiced: 0,
      paid: 0,
    };
    entry.visits += row.visits;
    entry.registrations += row.registrations;
    entry.practiced += row.practicedRegistrations;
    entry.paid += row.paidRegistrations;
    sources.set(source, entry);
  }
  const topSources = Array.from(sources.values())
    .sort(
      (left, right): number =>
        right.registrations - left.registrations || right.visits - left.visits,
    )
    .slice(0, 10);

  return (
    <Paper component="section" sx={{ p: { xs: 2, md: 3 }, mt: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {labels.title}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {labels.periodNote}
      </Typography>
      {attributionLoading && <CircularProgress size={28} />}
      {attributionError && <Alert severity="error">{labels.error}</Alert>}
      {attribution && (
        <>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
            <Typography>
              {labels.visits}: <strong>{totals.visits}</strong>
            </Typography>
            <Typography>
              {labels.registrations}: <strong>{totals.registrations}</strong>
            </Typography>
            <Typography>
              {labels.unknownRegistrations}: <strong>{totals.unknown}</strong>
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            {labels.ratioNote}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {labels.activationNote}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {labels.paidNote}
          </Typography>
          <FormControl sx={{ minWidth: 240, mb: 2 }} size="small">
            <InputLabel id="campaign-country-filter">
              {labels.countryFilter}
            </InputLabel>
            <Select
              labelId="campaign-country-filter"
              value={countryFilter}
              label={labels.countryFilter}
              onChange={(event): void => {
                dispatch(setAnalyticsCountry(event.target.value));
              }}
            >
              <MenuItem value="all">{labels.allCountries}</MenuItem>
              {Array.from(
                new Set(
                  [
                    ...attribution.countries,
                    ...(attribution.recordedRevenue ?? []),
                  ].map((row): string | null => row.country),
                ),
              ).map(
                (country): React.ReactElement => (
                  <MenuItem key={country ?? ""} value={country ?? ""}>
                    {countryName(country)}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
          <Typography component="h3" variant="h6">
            {labels.topSources}
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ my: 1 }}>
            {labels.topSourcesNote}
          </Typography>
          {topSources.length > 0 && (
            <Box sx={{ height: 320 }} role="img" aria-label={labels.topSources}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topSources}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="source" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="registrations"
                    name={labels.registrations}
                    fill="#1976d2"
                  />
                  <Bar
                    dataKey="practiced"
                    name={labels.practiced}
                    fill="#2e7d32"
                  />
                  <Bar dataKey="paid" name={labels.paid} fill="#9c27b0" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
          <Accordion
            sx={{ mt: 2 }}
            expanded={detailsExpanded}
            onChange={(_event, expanded): void => setDetailsExpanded(expanded)}
          >
            <AccordionSummary>{labels.utmDetails}</AccordionSummary>
            <AccordionDetails>
              <AnalyticsRowsTable
                label={labels.title}
                rows={rows}
                columns={[
                  {
                    label: labels.source,
                    value: (row): string => row.source ?? labels.unknown,
                  },
                  {
                    label: labels.medium,
                    value: (row): string => row.medium ?? "—",
                  },
                  {
                    label: labels.campaign,
                    value: (row): string => row.campaign ?? "—",
                  },
                  {
                    label: labels.content,
                    value: (row): string => row.content ?? "—",
                  },
                  {
                    label: labels.referrer,
                    value: (row): string => row.referrerHost ?? "—",
                  },
                  { label: labels.visits, value: (row): number => row.visits },
                  {
                    label: labels.registrations,
                    value: (row): number => row.registrations,
                  },
                  {
                    label: labels.ratio,
                    value: (row): string =>
                      analyticsRatio(row.registrationToVisitRatio),
                  },
                  {
                    label: labels.practiced,
                    value: (row): number => row.practicedRegistrations,
                  },
                  {
                    label: labels.activation,
                    value: (row): string => analyticsRatio(row.activationRate),
                  },
                  {
                    label: labels.paid,
                    value: (row): number => row.paidRegistrations,
                  },
                  {
                    label: labels.paymentRate,
                    value: (row): string => analyticsRatio(row.paymentRate),
                  },
                ]}
              />
            </AccordionDetails>
          </Accordion>
          {rows.length === 0 && (
            <Typography sx={{ mt: 2 }}>{labels.empty}</Typography>
          )}
          <Typography component="h3" variant="h6" sx={{ mt: 3 }}>
            {labels.revenueTitle}
          </Typography>
          <Typography variant="body2" sx={{ my: 2 }}>
            {labels.revenueNote}
          </Typography>
          {(attribution.recordedRevenue ?? []).filter(
            (row): boolean =>
              countryFilter === "all" || (row.country ?? "") === countryFilter,
          ).length === 0 ? (
            <Typography>{labels.revenueEmpty}</Typography>
          ) : (
            <AnalyticsRowsTable
              label={labels.revenueTitle}
              rows={(attribution.recordedRevenue ?? []).filter(
                (row): boolean =>
                  countryFilter === "all" ||
                  (row.country ?? "") === countryFilter,
              )}
              columns={[
                {
                  label: labels.source,
                  value: (row): string =>
                    row.source ?? row.referrerHost ?? labels.unknown,
                },
                {
                  label: labels.campaign,
                  value: (row): string => row.campaign ?? "—",
                },
                {
                  label: labels.content,
                  value: (row): string => row.content ?? "—",
                },
                {
                  label: labels.country,
                  value: (row): string => countryName(row.country),
                },
                { label: copy.currency, value: (row): string => row.currency },
                { label: copy.count, value: (row): number => row.count },
                {
                  label: copy.amount,
                  value: (row): string =>
                    row.amount.toLocaleString("en", {
                      maximumFractionDigits: 2,
                    }),
                },
              ]}
            />
          )}
          <Typography component="h3" variant="h6" sx={{ mt: 3 }}>
            {labels.countriesTitle}
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ my: 2 }}>
            {labels.countryNote}
          </Typography>
          <TableContainer>
            <Table size="small" aria-label={labels.countriesTitle}>
              <TableHead>
                <TableRow>
                  {[labels.country, labels.visits, labels.registrations].map(
                    (label) => (
                      <TableCell key={label}>{label}</TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {attribution.countries.map((row) => (
                  <TableRow key={row.country ?? ""}>
                    <TableCell>{countryName(row.country)}</TableCell>
                    <TableCell>{row.visits}</TableCell>
                    <TableCell>{row.registrations}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
}
